import type { StatusTone } from "@/lib/status-colors"
import { t } from "@/lib/i18n"

/**
 * Shape, endpoints and arithmetic for suppliers, purchase orders and receiving.
 *
 * ## There is no unit conversion, anywhere, and this file is where that shows
 *
 * A supply carries exactly one unit (`location_supplies.unit`). A purchase line
 * has no unit column, `PurchaseLine#apply_to_stock!` hands `quantity` straight
 * to the ledger untranslated, and no `conversion_factor`, `pack_size` or
 * `purchase_unit` exists in the schema. A restaurant that buys a 20 kg sack and
 * counts in grams therefore has to enter 20000 — and a quantity field labelled
 * only "cantidad" is exactly how somebody enters 20 instead.
 *
 * So the unit travels with every supply and is rendered beside every quantity,
 * and the pack helper below turns "3 costales × 20000 g a $240 el costal" into
 * the base-unit figures the endpoint takes. **The pack is a typing aid, not a
 * stored fact**: nothing downstream reads it, and storing it would be the first
 * half of a conversion mechanism this feature deliberately does not have.
 *
 * ## Money travels in pesos, as exact decimal strings
 *
 * The server emits `to_s("F")` and the browser formats. Nothing here crosses to
 * cents — `Staff::ReportSerializer` owns that basis and this screen never
 * touches it, which is what keeps `total_cost` (pesos) from being handed to a
 * helper that expects cents and rendering 100× the invoice.
 */

export type SupplyUnit = "gram" | "kilogram" | "milliliter" | "liter" | "piece"

/**
 * `draft` is an expectation and moves nothing. `received` moved stock and
 * repointed every average cost. `cancelled` is an expectation that died with a
 * name and a reason on it.
 */
export type PurchaseStatus = "draft" | "received" | "cancelled"

export type Supplier = {
  id: number
  name: string
  contactName: string | null
  phone: string | null
  active: boolean
  /** How many deliveries name this supplier. Present on the suppliers screen. */
  purchaseCount?: number
  /** False once a delivery names it: the purchase has to keep saying who it came from. */
  destroyable?: boolean
}

export type PurchaseLine = {
  id: number
  supplyId: number
  supplyName: string
  unit: SupplyUnit
  unitLabel: string
  /** Exact decimal string, in the supply's own unit. */
  quantity: string
  /** Exact decimal string, pesos per one of that unit. */
  unitCost: string
  lineCost: string
}

export type Purchase = {
  id: number
  status: PurchaseStatus
  statusLabel: string
  invoiceNumber: string | null
  supplierId: number
  supplierName: string
  lineCount: number
  /** Pesos, exact decimal string. Never cents. */
  totalCost: string
  createdAt: string | null
  receivedAt: string | null
  receivedByName: string | null
  cancelledAt: string | null
  cancelledByName: string | null
  cancelReason: string | null
  cancelReasonLabel: string | null
  /** The model's own answer: a draft with at least one line. */
  receivable: boolean
  /** The model's own answer: only a draft can be cancelled. */
  cancellable: boolean
  /** Only on the detail view. */
  lines?: PurchaseLine[]
}

export type SupplyOption = {
  id: number
  name: string
  unit: SupplyUnit
  unitLabel: string
  averageCost: string
}

export type PurchasingOption = {
  value: string
  label: string
}

export const PURCHASING_PATHS = {
  suppliers: "/manage/suppliers",
  createSupplier: "/manage/suppliers",
  supplier: (supplierId: number) => `/manage/suppliers/${supplierId}`,
  purchases: "/manage/purchases",
  newPurchase: "/manage/purchases/new",
  createPurchase: "/manage/purchases",
  purchase: (purchaseId: number) => `/manage/purchases/${purchaseId}`,
  receivePurchase: (purchaseId: number) => `/manage/purchases/${purchaseId}/receive`,
  cancelPurchase: (purchaseId: number) => `/manage/purchases/${purchaseId}/cancel`,
} as const

/** The location feature that has to be on for any of this to answer at all. */
export const PURCHASING_FEATURE = "purchasing"

/** `location_purchase_lines.quantity` is `decimal(12,3)`. */
export const QUANTITY_SCALE = 3

/** `location_purchase_lines.unit_cost` is `decimal(12,4)` — four decimals is what makes salt at MXN 12/kg storable as 0.0120 per gram rather than rounding to zero. */
export const UNIT_COST_SCALE = 4

const I18N_PREFIX = "ui.react.features.staff.purchasing"

export function tPurchasing(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * The tone for a delivery's status mark.
 *
 * `draft` is `reserved` rather than `warning`: a purchase order waiting for its
 * truck is the normal state of a purchase order, not a problem. `received` is
 * the only one that moved money and stock.
 */
export function purchaseTone(status: string): StatusTone {
  if (status === "received") return "available"
  if (status === "cancelled") return "disabled"
  return "reserved"
}

/**
 * Drops the trailing zeros a fixed-scale decimal carries.
 *
 * `20.000` is the same quantity as `20` and the second is the one a manager
 * typed. Applied to display and to the values the pack helper writes into the
 * form, never to anything parsed for arithmetic.
 */
export function trimDecimal(value: string): string {
  if (!value.includes(".")) return value

  return value.replace(/0+$/, "").replace(/\.$/, "")
}

/** A quantity is meaningless without the unit it is counted in. Always together. */
export function formatQuantity(value: string, unitLabel: string): string {
  return `${trimDecimal(value)} ${unitLabel}`
}

function parsePositive(value: string): number | null {
  const parsed = Number(String(value).trim())

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Base-unit quantity from "how many packages" × "how much is in one".
 *
 * Returns `""` when either half is missing, so an incomplete helper never
 * overwrites a quantity the operator typed by hand.
 */
export function packQuantity(packs: string, unitsPerPack: string): string {
  const packCount = parsePositive(packs)
  const perPack = parsePositive(unitsPerPack)

  if (packCount === null || perPack === null) return ""

  return trimDecimal((packCount * perPack).toFixed(QUANTITY_SCALE))
}

/**
 * Cost of one base unit from the price of one package.
 *
 * A case of 24 pieces at MXN 100 gives `4.1667`, and 24 × 4.1667 is 100.0008 —
 * eight ten-thousandths of a peso on a hundred-peso case. That residue is a
 * rounding artifact of a four-decimal column, not a modelling failure, and it is
 * the price of having no conversion table.
 */
export function packUnitCost(packPrice: string, unitsPerPack: string): string {
  const trimmedPrice = String(packPrice).trim()
  const price = Number(trimmedPrice)
  const perPack = parsePositive(unitsPerPack)

  // `Number("")` is 0, and 0 is a legitimate package price — a delivery can be
  // free. Checking the string first is what keeps an empty field from quietly
  // computing a unit cost of zero and recording the whole delivery as a gift.
  if (trimmedPrice === "") return ""
  if (!Number.isFinite(price) || price < 0 || perPack === null) return ""

  return trimDecimal((price / perPack).toFixed(UNIT_COST_SCALE))
}

/** What one line will cost, for the running total on the form. */
export function lineTotal(quantity: string, unitCost: string): number {
  const parsedQuantity = Number(String(quantity).trim())
  const parsedCost = Number(String(unitCost).trim())

  if (!Number.isFinite(parsedQuantity) || !Number.isFinite(parsedCost)) return 0

  return parsedQuantity * parsedCost
}

export type PurchaseLineDraft = {
  supplyId: string
  quantity: string
  unitCost: string
}

/**
 * Whether one line has what `Location::PurchaseLine` validates: a supply, a
 * quantity greater than zero and a cost that is not negative. Free goods are a
 * real thing, so zero is a valid cost and only a missing one is refused.
 */
export function purchaseLineComplete(line: PurchaseLineDraft): boolean {
  if (!line.supplyId) return false
  if (parsePositive(line.quantity) === null) return false

  const cost = Number(String(line.unitCost).trim())

  return line.unitCost.trim() !== "" && Number.isFinite(cost) && cost >= 0
}

/**
 * Whether the form has everything the endpoint needs.
 *
 * An empty delivery is refused here as well as on the server: `receive!` raises
 * `EmptyPurchaseError` on one, so a purchase saved with no lines is a row that
 * can only ever be cancelled.
 */
export function purchaseRequestComplete(input: {
  supplierId: string
  lines: PurchaseLineDraft[]
}): boolean {
  if (!input.supplierId) return false
  if (input.lines.length === 0) return false

  return input.lines.every(purchaseLineComplete)
}

/** The running total of a form in progress, in pesos. */
export function purchaseDraftTotal(lines: PurchaseLineDraft[]): number {
  return lines.reduce((total, line) => total + lineTotal(line.quantity, line.unitCost), 0)
}
